/**
 * Inline SVG response-time chart. Hand-rolled to avoid pulling in a
 * full charting library. Single accent line (avg) with a faded p95
 * envelope on top, crosshair tooltip on hover.
 */

import { useMemo, useState } from "react";

import type { MetricsBucket } from "../api/status";

interface Props {
  buckets: MetricsBucket[];
}

function niceMax(n: number): number {
  if (n <= 0) return 100;
  const pow = 10 ** Math.floor(Math.log10(n));
  return Math.ceil(n / pow) * pow;
}

function fmtTs(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ACCENT = "#6a9fd8"; // dr.blue — "tech / fiabilité"
const P95 = "#f59e0b";

export function ResponseTimeChart({ buckets }: Props): JSX.Element {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const geom = useMemo(() => {
    const W = 1000;
    const H = 240;
    const padding = { top: 16, right: 16, bottom: 28, left: 40 };
    const innerW = W - padding.left - padding.right;
    const innerH = H - padding.top - padding.bottom;
    const xs = buckets.map((_, i) =>
      buckets.length === 1 ? innerW / 2 : (i / (buckets.length - 1)) * innerW,
    );
    const maxY = niceMax(
      Math.max(
        1,
        ...buckets.map((b) => Math.max(b.avgLatencyMs ?? 0, b.p95LatencyMs ?? 0)),
      ),
    );
    return { W, H, padding, innerW, innerH, xs, maxY };
  }, [buckets]);

  if (buckets.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        Not enough data yet — the chart populates as probes accumulate.
      </p>
    );
  }

  const { W, H, padding, innerW, innerH, xs, maxY } = geom;
  const y = (v: number | null): number | null =>
    v === null ? null : innerH - (v / maxY) * innerH;

  function path(values: (number | null)[]): string {
    let d = "";
    let pen: "M" | "L" = "M";
    for (let i = 0; i < values.length; i++) {
      const yv = y(values[i]!);
      if (yv === null) {
        pen = "M";
        continue;
      }
      d += `${pen}${xs[i]!.toFixed(1)},${yv.toFixed(1)} `;
      pen = "L";
    }
    return d.trim();
  }

  // Area under the avg line — gives the chart visual weight without
  // distracting from the line itself.
  function area(values: (number | null)[]): string {
    const segs: string[] = [];
    let start = -1;
    for (let i = 0; i <= values.length; i++) {
      const yv = i < values.length ? y(values[i]!) : null;
      if (yv === null) {
        if (start >= 0) {
          // Close the previous segment.
          let seg = `M${xs[start]!.toFixed(1)},${innerH}`;
          for (let j = start; j < i; j++) {
            seg += `L${xs[j]!.toFixed(1)},${y(values[j]!)!.toFixed(1)}`;
          }
          seg += `L${xs[i - 1]!.toFixed(1)},${innerH}Z`;
          segs.push(seg);
          start = -1;
        }
      } else if (start < 0) {
        start = i;
      }
    }
    return segs.join(" ");
  }

  const avgValues = buckets.map((b) => b.avgLatencyMs);
  const p95Values = buckets.map((b) => b.p95LatencyMs);
  const pAvg = path(avgValues);
  const pP95 = path(p95Values);
  const aAvg = area(avgValues);

  const ticks = [0, Math.round(maxY / 2), maxY];

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W - padding.left;
    // Snap to nearest data point.
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < xs.length; i++) {
      const d = Math.abs(xs[i]! - px);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHoverIdx(best);
  }

  const hover = hoverIdx !== null ? buckets[hoverIdx] : null;
  const hoverX = hoverIdx !== null ? xs[hoverIdx]! : null;
  const hoverY = hover ? y(hover.avgLatencyMs) : null;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full select-none"
        role="img"
        aria-label="Response time"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="rtc-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.28" />
            <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
          </linearGradient>
        </defs>
        <g transform={`translate(${padding.left},${padding.top})`}>
          {ticks.map((t) => {
            const ty = innerH - (t / maxY) * innerH;
            return (
              <g key={t}>
                <line
                  x1={0}
                  x2={innerW}
                  y1={ty}
                  y2={ty}
                  stroke="currentColor"
                  strokeOpacity={0.07}
                />
                <text
                  x={-8}
                  y={ty + 4}
                  fontSize={11}
                  fill="currentColor"
                  opacity={0.45}
                  textAnchor="end"
                >
                  {t}ms
                </text>
              </g>
            );
          })}
          <path d={aAvg} fill="url(#rtc-fill)" />
          <path
            d={pP95}
            fill="none"
            stroke={P95}
            strokeOpacity={0.45}
            strokeWidth={1.25}
            strokeDasharray="3 3"
          />
          <path d={pAvg} fill="none" stroke={ACCENT} strokeWidth={2} />

          {hoverX !== null && (
            <g>
              <line
                x1={hoverX}
                x2={hoverX}
                y1={0}
                y2={innerH}
                stroke={ACCENT}
                strokeOpacity={0.35}
                strokeDasharray="2 2"
              />
              {hoverY !== null && (
                <circle
                  cx={hoverX}
                  cy={hoverY}
                  r={4}
                  fill={ACCENT}
                  stroke="white"
                  strokeWidth={1.5}
                />
              )}
            </g>
          )}

          <text
            x={innerW}
            y={innerH + 18}
            fontSize={11}
            fill="currentColor"
            opacity={0.5}
            textAnchor="end"
          >
            {fmtTs(buckets[buckets.length - 1]!.ts)}
          </text>
          <text
            x={0}
            y={innerH + 18}
            fontSize={11}
            fill="currentColor"
            opacity={0.5}
          >
            {fmtTs(buckets[0]!.ts)}
          </text>
        </g>
      </svg>

      {hover && hoverX !== null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-md border border-white/10 bg-dr-bg/95 px-3 py-2 text-xs shadow-lg backdrop-blur"
          style={{
            left: `calc(${((hoverX + padding.left) / W) * 100}% )`,
            top: 0,
          }}
        >
          <p className="text-[10px] uppercase tracking-wide text-text-muted">
            {fmtTs(hover.ts)}
          </p>
          <p className="mt-1 font-mono text-dr-heading">
            avg {hover.avgLatencyMs ?? "—"}ms
            {hover.p95LatencyMs !== null && (
              <span className="ml-2 text-text-muted">
                p95 {hover.p95LatencyMs}ms
              </span>
            )}
          </p>
          <p className="text-[10px] text-text-muted">
            {(hover.okRatio * 100).toFixed(1)}% ok · {hover.totalProbes} probes
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 text-[11px] text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[2px] w-4 rounded"
            style={{ backgroundColor: ACCENT }}
          />
          Avg latency
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[2px] w-4 rounded"
            style={{
              backgroundImage: `repeating-linear-gradient(to right, ${P95} 0 3px, transparent 3px 6px)`,
            }}
          />
          p95
        </span>
      </div>
    </div>
  );
}
